import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, ReactNode } from 'react';
import { supabase, isSupabaseConfigured } from '../lib/supabase';
import { signDocumentUrls } from '../lib/portalStorage';
import { InstructorLook, mapInstructorLook } from '../lib/instructorLook';
import { useRefreshable } from './RefreshContext';
import {
  PortalProgram,
  PortalClass,
  PortalUpdate,
  PortalEvent,
  PortalDocument,
} from '../types';
import {
  ProgramSlug,
  PROGRAM_CLASS_CATEGORIES,
  hasPortalAccess,
  grantPortalAccess,
  revokePortalAccess,
} from '../lib/portal';
// Shared with PortalAdminContext, which reads the same tables as a signed-in
// author. One mapper per table, so the two sides cannot drift.
import {
  mapProgram,
  mapClass,
  mapUpdate,
  mapEvent,
  mapDocument,
} from '../lib/portalMappers';

/**
 * Data for the parent portal.
 *
 * Mounted around /portal routes (see App.tsx) rather than at the app root, so
 * the portal owns its own fetching and nothing here runs for staff pages.
 *
 * WHAT A PARENT'S DEVICE ACTUALLY REQUESTS
 *
 * Mounting this narrowly does not, on its own, stop the staff contexts from
 * firing: DataProvider sits above the Router in App.tsx, so its children mount
 * regardless of route. That used to cost a parent 13 Supabase requests per cold
 * load, 6 of them to staff tables RLS returns nothing for, plus two failures.
 *
 * Every data context now short-circuits both its load AND its realtime
 * subscription when there is no session — WorkHoursContext first, then
 * EventContext, SOPContext, TaskContext and the profiles channel in
 * AuthContext. Measured signed out, against a PRODUCTION BUILD:
 *
 *     /portal/:program              3 requests
 *       portal_programs, portal_updates, portal_events
 *     /portal/:program/classes/:id  4 requests
 *       portal_programs, portal_classes, portal_updates, portal_documents
 *
 *     0 staff-table requests   0 console errors   0 websocket attempts
 *
 * MEASURE THIS AGAINST `npm run build`, NOT `npm start`
 *
 * Both numbers roughly double on the dev server, because StrictMode mounts
 * every provider twice — the same 3 reads look like 7 and neither figure is
 * what a parent's phone does. Worse, HMR keeps the pre-edit module mounted, so
 * the console can report requests the new code no longer makes AND requests it
 * does. A guard was once declared working on that basis and was not.
 *
 * There is a `sop-app-build` entry in .claude/launch.json that serves build/ on
 * :3004 for exactly this. Rebuild first — anything that ran a build from a
 * stashed tree has left the wrong bundle in there.
 *
 * THE SUBSCRIPTIONS WERE THE HALF THAT GOT MISSED
 *
 * The earlier pass fixed the fetches and left four `.channel(...).subscribe()`
 * calls guarded only by `if (!useSupabase) return`. A realtime channel opened
 * with the anon key cannot receive anything RLS would allow, so it fails — and
 * then retries on a backoff for as long as the page is open. On a class page
 * that was seven failed websockets, on the device this app is mostly used from.
 * A subscription is a request that never stops, so it needs the guard more than
 * a fetch does, not less.
 *
 * If these numbers climb again, the cause is a staff context fetching or
 * subscribing on mount without a session guard; AuthProvider is above
 * DataProvider, so any of them can check first. The portal_programs x3 is this
 * file, PortalAdminProvider and the gate all reading the same public table —
 * harmless, but it is where to start if someone wants 7 to be 5.
 *
 * Every read here goes through the signed-in account — v30 closed the anon door
 * — and since v55 the answer depends on which account: the All-Star section,
 * its classes, posts, events and files come back only for an All-Star family or
 * staff. So nothing in this file filters for access-control reasons; the
 * policies do. The filtering that IS here is for ordering and grouping.
 */

interface PortalContextValue {
  programs: PortalProgram[];
  loading: boolean;
  error: string | null;
  getProgramBySlug: (slug: string) => PortalProgram | undefined;

  /**
   * A signed URL for each program's hero, keyed by program id.
   *
   * Signed once with the programs rather than per page, because the picture is
   * on the program overview AND on the tile that leads to it, and signing it
   * twice would be two requests for one image. Missing means either no picture
   * or a signing that failed; both render the same and neither is an error a
   * parent is told about.
   */
  heroUrls: Record<string, string>;

  /**
   * Teacher marks, keyed by normalised instructor name. Empty is normal — see
   * lib/instructorLook.ts, where a missing row is the default rather than a
   * missing feature.
   */
  instructorLooks: Record<string, InstructorLook>;

  /** Has this device cleared the access code for this program? */
  hasAccess: (slug: ProgramSlug) => boolean;
  /** Verify a code against the database. Returns true on success and records it. */
  verifyCode: (slug: ProgramSlug, code: string) => Promise<boolean>;
  forgetAccess: (slug: ProgramSlug) => void;

  /**
   * Every class shown on a program's schedule.
   *
   * Keyed by slug rather than program id: the All-Star schedule lists Academy
   * and TNT classes too, and those rows are filed under a different program.
   */
  fetchClasses: (slug: ProgramSlug) => Promise<PortalClass[]>;
  fetchUpdates: (programId: string, classId?: string | null) => Promise<PortalUpdate[]>;
  fetchEvents: (programId: string) => Promise<PortalEvent[]>;
  fetchDocuments: (programId: string, classId?: string | null) => Promise<PortalDocument[]>;
  /** Short-lived signed URL for a document in the private bucket. */
  getDocumentUrl: (storagePath: string) => Promise<string | null>;
  /**
   * The same, for a whole page of files in one request.
   *
   * Photos and videos are rendered rather than tapped, so their URLs have to
   * exist before the parent touches anything. Keyed by storage path; a path
   * missing from the result could not be signed.
   */
  getDocumentUrls: (storagePaths: string[]) => Promise<Record<string, string>>;
}

const PortalContext = createContext<PortalContextValue | undefined>(undefined);

export const usePortal = () => {
  const ctx = useContext(PortalContext);
  if (!ctx) throw new Error('usePortal must be used within a PortalProvider');
  return ctx;
};

// ------------------------------------------------------------- provider

export const PortalProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [programs, setPrograms] = useState<PortalProgram[]>([]);
  const [heroUrls, setHeroUrls] = useState<Record<string, string>>({});
  const [instructorLooks, setInstructorLooks] = useState<Record<string, InstructorLook>>({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Bumped by verifyCode so components re-read hasAccess, which is backed by
  // localStorage rather than state and would not otherwise trigger a render.
  const [accessVersion, setAccessVersion] = useState(0);

  /**
   * The two optional reads that make the portal look like something.
   *
   * Swallows everything. Every failure mode here degrades to the pre-v46
   * appearance, which is a page that works — so there is nothing to tell a
   * parent and nothing they could do about it. It also means this is safe to
   * run against a database where v46 has not been applied: the table 404s, the
   * catch fires, and the schedule renders with default marks.
   */
  const loadLook = useCallback(async (loaded: PortalProgram[]) => {
    if (!supabase) return;

    try {
      const { data, error: err } = await supabase
        .from('portal_instructor_looks')
        .select('name_key, display_name, mode, initials, icon_key, palette_key, shape, pattern, ring');

      if (!err && data) {
        const byKey: Record<string, InstructorLook> = {};
        data.forEach((row: any) => {
          const look = mapInstructorLook(row);
          byKey[look.nameKey] = look;
        });
        setInstructorLooks(byKey);
      }
    } catch {
      /* Default marks. Nothing to say. */
    }

    // One signing call for every program that has a picture, or none at all.
    const paths = loaded.map(p => p.heroPath).filter((v): v is string => !!v);
    if (paths.length === 0) return;

    try {
      const signed = await signDocumentUrls(paths);
      const byProgram: Record<string, string> = {};
      loaded.forEach(p => {
        const url = p.heroPath ? signed[p.heroPath] : undefined;
        if (url) byProgram[p.id] = url;
      });
      setHeroUrls(byProgram);
    } catch {
      /* No picture. The page is the one it was before. */
    }
  }, []);

  // Bumped by every programs read. An answer whose number is no longer current
  // was asked for by whoever was signed in before, and is dropped rather than
  // painted over the list for whoever is signed in now.
  const programsRequest = useRef(0);

  const loadPrograms = useCallback(async () => {
    if (!isSupabaseConfigured() || !supabase) {
      setError('The portal is not available right now.');
      setLoading(false);
      return;
    }

    const requestId = ++programsRequest.current;
    const stale = () => requestId !== programsRequest.current;

    try {
      const { data, error: err } = await supabase
        .from('portal_programs')
        .select('*')
        .eq('is_active', true)
        .order('sort_order', { ascending: true });

      if (stale()) return;
      if (err) throw err;

      const mapped = (data ?? []).map(mapProgram);
      setPrograms(mapped);
      setError(null);

      // THE LOOK IS LOADED AFTER THE CONTENT, AND NEVER BLOCKS IT.
      //
      // Both of these are decoration: a hero that does not arrive leaves the
      // page exactly as it was before v46, and a missing looks table leaves
      // every teacher on the deterministic default. So they are awaited
      // separately from the program fetch, after `error` has already been
      // cleared and `loading` is about to end — a failure here must never turn
      // into "Could not load the portal".
      //
      // Cost, measured against the accounting at the top of this file: ONE
      // extra request per portal session for the looks, plus one for the
      // signing when a program actually has a picture. Not per page — both
      // live in this provider for as long as the portal is open.
      void loadLook(mapped);
    } catch (e: any) {
      if (stale()) return;
      console.error('Failed to load portal programs:', e);
      setError('Could not load the portal. Please try again.');
    } finally {
      if (!stale()) setLoading(false);
    }
    // loadLook is itself stable, so naming it here keeps loadPrograms stable
    // too — which matters, because useRefreshable below is keyed on its
    // identity and a new function each render would re-register the loader.
  }, [loadLook]);

  useEffect(() => {
    loadPrograms();
  }, [loadPrograms]);

  /**
   * WHICH SECTIONS THERE ARE DEPENDS ON WHO IS SIGNED IN.
   *
   * Since v55 portal_programs returns the All-Star section only to an All-Star
   * family or to staff, so this list is an answer about one account. The
   * provider outlives a sign-in — /portal/login is a page inside it, and
   * signing in navigates rather than reloads — so without this, the list read
   * on the login screen, as nobody, is the list a signed-in Academy family is
   * handed: an All-Star tile, and a gate that lets them through it to a section
   * the database then shows nothing of.
   *
   * Only a change of ACCOUNT reloads. TOKEN_REFRESHED fires this listener every
   * hour and changes nothing about what the account may read. The list is
   * cleared while the new one loads, so the tiles and the gate show their
   * loading state rather than the previous account's sections.
   *
   * Dispatched with setTimeout for the rule PortalAuthContext documents: never
   * call supabase inside the callback itself, or the auth lock is still held.
   */
  useEffect(() => {
    if (!isSupabaseConfigured() || !supabase) return;

    // undefined until the first event says who is signed in at mount.
    let account: string | null | undefined;

    const { data: sub } = supabase.auth.onAuthStateChange((_event: string, session: any) => {
      const next: string | null = session?.user?.id ?? null;
      if (account === undefined || next === account) {
        account = next;
        return;
      }
      account = next;
      setTimeout(() => {
        setPrograms([]);
        setHeroUrls({});
        setLoading(true);
        void loadPrograms();
      }, 0);
    });

    return () => sub?.subscription?.unsubscribe?.();
  }, [loadPrograms]);

  // The portal has no realtime channel, on purpose (see useProgramQuery). The
  // header button, pull-to-refresh and returning to the foreground are how a
  // parent gets a post made after they opened the app — this is the programs
  // half; each page registers its own feed.
  useRefreshable(loadPrograms);

  const getProgramBySlug = useCallback(
    (slug: string) => programs.find(p => p.slug === slug),
    [programs]
  );

  // ------------------------------------------------------------ access

  const hasAccess = useCallback(
    (slug: ProgramSlug) => {
      const program = programs.find(p => p.slug === slug);
      // A program that does not require a code is open to everyone. While the
      // list is still loading, program is undefined and this returns the stored
      // flag — the gate shows a spinner until loading finishes, so an
      // ungated program is never briefly gated.
      if (program && !program.requiresCode) return true;
      return hasPortalAccess(slug);
    },
    // accessVersion is a deliberate dependency: it is how a successful
    // verifyCode propagates to components reading this.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [programs, accessVersion]
  );

  /**
   * Check a code against verify_portal_code().
   *
   * The comparison happens inside Postgres against a bcrypt hash that has no
   * grants and no RLS policy, so the hash is never sent to the browser and the
   * browser never learns anything but true/false.
   */
  const verifyCode = useCallback(async (slug: ProgramSlug, code: string) => {
    if (!isSupabaseConfigured() || !supabase) return false;

    try {
      const { data, error: err } = await supabase.rpc('verify_portal_code', {
        p_slug: slug,
        p_code: code,
      });

      if (err) {
        console.error('verify_portal_code failed:', err);
        return false;
      }

      if (data === true) {
        grantPortalAccess(slug);
        setAccessVersion(v => v + 1);
        return true;
      }
      return false;
    } catch (e) {
      console.error('verify_portal_code threw:', e);
      return false;
    }
  }, []);

  const forgetAccess = useCallback((slug: ProgramSlug) => {
    revokePortalAccess(slug);
    setAccessVersion(v => v + 1);
  }, []);

  // ----------------------------------------------------------- content

  /**
   * Filtered by category, not by program.
   *
   * A parent on the All-Star schedule is meant to see the whole studio, and
   * an Academy class belongs to the Academy program — so filtering by
   * program_id here would show them 30 company routines and nothing else.
   * Which categories belong to which schedule is PROGRAM_CLASS_CATEGORIES.
   *
   * Ordered by the schedule rather than by sort_order, because that is the
   * order every view wants and re-sorting 102 rows on a phone is work the
   * index has already done.
   */
  const fetchClasses = useCallback(async (slug: ProgramSlug) => {
    const { data, error: err } = await supabase
      .from('portal_classes')
      .select('*')
      .in('category', PROGRAM_CLASS_CATEGORIES[slug])
      .eq('is_active', true)
      .order('day_of_week', { ascending: true, nullsFirst: false })
      .order('start_time', { ascending: true, nullsFirst: false })
      .order('name', { ascending: true });

    if (err) throw err;
    return (data ?? []).map(mapClass);
  }, []);

  const fetchUpdates = useCallback(
    async (programId: string, classId?: string | null) => {
      let query = supabase
        .from('portal_updates')
        .select('*')
        // This section's posts, and the ones sent to everyone (v55), which
        // belong to no one section and so are listed in both.
        .or(`program_id.eq.${programId},program_id.is.null`)
        .eq('is_published', true)
        // Broadcasts only. A note addressed to one household (v36) is already
        // unreadable by anon and by other families, but its own family WOULD
        // see it here — appearing on the program feed among notices sent to
        // ninety people, with nothing to say it was written to them. It belongs
        // in their profile's Updates card, which labels it.
        .is('household_id', null);

      // undefined = everything for the program; null = program-wide only;
      // a string = that class only.
      if (classId === null) query = query.is('class_id', null);
      else if (typeof classId === 'string') query = query.eq('class_id', classId);

      const { data, error: err } = await query
        .order('is_pinned', { ascending: false })
        .order('published_at', { ascending: false, nullsFirst: false });

      if (err) throw err;
      return (data ?? []).map(mapUpdate);
    },
    []
  );

  const fetchEvents = useCallback(async (programId: string) => {
    // A month back so a calendar opened on the 1st still shows the tail of the
    // previous month; a year forward covers a full competition season.
    const from = new Date();
    from.setMonth(from.getMonth() - 1);
    const to = new Date();
    to.setFullYear(to.getFullYear() + 1);

    const { data, error: err } = await supabase
      .from('portal_events')
      .select('*')
      .eq('program_id', programId)
      .eq('is_published', true)
      .gte('starts_at', from.toISOString())
      .lte('starts_at', to.toISOString())
      .order('starts_at', { ascending: true });

    if (err) throw err;
    return (data ?? []).map(mapEvent);
  }, []);

  /**
   * `classId` mirrors fetchUpdates: omitted means every file in the program,
   * null means studio-wide only, a string means that one class. Undefined and
   * null are deliberately different here — `.is('class_id', null)` and no filter
   * at all are not the same query.
   */
  const fetchDocuments = useCallback(async (programId: string, classId?: string | null) => {
    let query = supabase
      .from('portal_documents')
      .select('*')
      .eq('program_id', programId)
      // A file attached to an info post (v65) belongs under that post, not in
      // the class's own file list. loadUpdateFiles is what fetches those.
      .is('update_id', null)
      .eq('is_published', true);

    if (classId === null) query = query.is('class_id', null);
    else if (typeof classId === 'string') query = query.eq('class_id', classId);

    const { data, error: err } = await query
      .order('sort_order', { ascending: true })
      .order('created_at', { ascending: false });

    if (err) throw err;
    return (data ?? []).map(mapDocument);
  }, []);

  /**
   * Sign a URL for a document.
   *
   * The bucket is private, so there is no public URL to link to. One hour is
   * long enough to open or download and short enough that a copied link does
   * not become a permanent handle on the file.
   */
  const getDocumentUrl = useCallback(async (storagePath: string) => {
    try {
      const { data, error: err } = await supabase.storage
        .from('portal-documents')
        .createSignedUrl(storagePath, 60 * 60);

      if (err) throw err;
      return data?.signedUrl ?? null;
    } catch (e) {
      console.error('Could not sign document URL:', e);
      return null;
    }
  }, []);

  /** Batched, for a page that has to render its files rather than list them. */
  const getDocumentUrls = useCallback(
    (storagePaths: string[]) => signDocumentUrls(storagePaths),
    []
  );

  const value = useMemo<PortalContextValue>(
    () => ({
      programs,
      heroUrls,
      instructorLooks,
      loading,
      error,
      getProgramBySlug,
      hasAccess,
      verifyCode,
      forgetAccess,
      fetchClasses,
      fetchUpdates,
      fetchEvents,
      fetchDocuments,
      getDocumentUrl,
      getDocumentUrls,
    }),
    [
      programs, heroUrls, instructorLooks, loading, error, getProgramBySlug, hasAccess, verifyCode,
      forgetAccess, fetchClasses, fetchUpdates, fetchEvents, fetchDocuments,
      getDocumentUrl, getDocumentUrls,
    ]
  );

  return <PortalContext.Provider value={value}>{children}</PortalContext.Provider>;
};
